import React, { FC, useEffect } from 'react';
import { VIEWER_HOOKS } from '@wix/pricing-plans-common/hooks';
import { useStyles } from '@wix/tpa-settings/react';
import { Error as ErrorIcon } from '@wix/wix-ui-icons-common/on-stage';
import { DatePickerInput, DatePickerInputLocale, TextFieldTheme, SectionNotification } from '@wix/wix-ui-tpa/cssVars';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { useLocale } from '../../../../hooks/useLocale';
import { doesLocalDateOverlapInterval } from '../../../../utils/date-constraints';
import componentStyles, { FormFieldBorderStyle } from '../../stylesParams';
import { st, classes } from './PlanStartDatePicker.st.css';

interface PlanStartDatePickerProps {
  value: Date | undefined;
  onChange: (value: Date) => void;
  className?: string;
  earliestStartDate?: Date;
  latestStartDateExclusive?: Date;
  disableAllDates?: boolean;
  noAvailableDatesMessage?: string;
  onLoad?: () => void;
}

const PlanStartDatePicker: FC<PlanStartDatePickerProps> = ({
  value,
  onChange,
  className,
  earliestStartDate,
  latestStartDateExclusive,
  disableAllDates,
  noAvailableDatesMessage,
  onLoad,
}) => {
  const styles = useStyles();
  const { locale } = useLocale();
  const { t } = useTranslation();

  useEffect(() => {
    onLoad?.();
  }, [onLoad]);

  return (
    <div className={className}>
      <DatePickerInput
        className={st(classes.root, {
          'no-border': styles.get(componentStyles.planFormFieldBorderStyle) === FormFieldBorderStyle.None,
          'bottom-border': styles.get(componentStyles.planFormFieldBorderStyle) === FormFieldBorderStyle.Bottom,
        })}
        inputTheme={
          styles.get(componentStyles.planFormFieldBorderStyle) === FormFieldBorderStyle.Bottom
            ? TextFieldTheme.Line
            : TextFieldTheme.Box
        }
        value={value}
        onChange={(_value) => onChange(new Date(_value))}
        label={t('plan-customization.widget.datepicker-label')}
        excludePastDates
        removeClearButton
        locale={locale as DatePickerInputLocale}
        data-hook={VIEWER_HOOKS.PLAN_CUSTOMIZATION_DATE_PICKER}
        disabled={disableAllDates}
        filterDate={
          disableAllDates || earliestStartDate || latestStartDateExclusive
            ? (date) =>
                !disableAllDates && doesLocalDateOverlapInterval(date, earliestStartDate, latestStartDateExclusive)
            : undefined
        }
      />
      {disableAllDates && (
        <SectionNotification
          className={classes.notification}
          type="error"
          size="compact"
          data-hook={VIEWER_HOOKS.PLAN_CUSTOMIZATION_DATE_PICKER_NOTIFICATION}
        >
          <SectionNotification.Icon icon={<ErrorIcon />} />
          <SectionNotification.Text>
            {noAvailableDatesMessage || t('plan-customization.widget.datepicker-no-available-dates')}
          </SectionNotification.Text>
        </SectionNotification>
      )}
    </div>
  );
};

export default PlanStartDatePicker;
